import type { MutableRefObject, ReactNode } from 'react';
import { RequiredLabel } from '@gonfalon/launchpad-experimental/RequiredLabel';
import { Text } from '@launchpad-ui/components';

import type { JSONSchema } from '../types';

import { ArrayFieldWrapper, ArrayItemRow } from './ArrayFieldComponents';
import { getDefaultValue } from './getDefaultValue';
import { schemaToZod } from './schemaToZod';

/**
 * Extracts human-readable error messages from TanStack Form field errors.
 * Errors may be strings or Zod-style issue objects with a `message` property.
 */
function formatFieldErrors(errors: unknown[]): string {
  return errors
    .map((e) => {
      if (typeof e === 'string') {
        return e;
      }
      if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
        return (e as { message: string }).message;
      }
      return JSON.stringify(e);
    })
    .join(', ');
}

/**
 * Options passed to field renderers.
 */
export interface FieldRendererOptions {
  /**
   * Whether the field is required (shows asterisk via RequiredLabel).
   */
  required?: boolean;

  /**
   * Override the label displayed for the field.
   * Used for array items to show numbered labels like "Tag 1", "Tag 2".
   */
  labelOverride?: string;

  /**
   * The form context hook (useFormContext from the form hook's contexts).
   * Required for rendering nested fields in object/array schemas.
   *
   * Note: This should NOT be called inside the render function returned by field renderers,
   * as that function runs inside TanStack Form's useMemo. Instead, use `formContext` for
   * renderers that need the form.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useFormContext?: () => any;

  /**
   * Pre-resolved form context instance.
   * Use this instead of calling useFormContext() inside render functions
   * to avoid React hooks rules violations.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formContext?: any;

  /**
   * Stable ref for array item keys. Must be created with useRef in the parent component
   * so it survives the renderer factory being re-called on each render.
   */
  stableKeysRef?: MutableRefObject<string[]>;

  /**
   * Stable ref for the key counter. Same lifetime requirement as stableKeysRef.
   */
  keyCounterRef?: MutableRefObject<number>;

  /**
   * Stable ref tracking which index was most recently added (for auto-focus).
   */
  newlyAddedIndexRef?: MutableRefObject<number | null>;
}

/**
 * A function that renders a form field for a given JSON Schema.
 * Returns a render function compatible with form.AppField children.
 *
 * The field parameter uses `any` to allow flexibility in the field API,
 * as different field types have different value types.
 */
export type FieldRenderer = (
  schema: JSONSchema,
  fieldName: string,
  options?: FieldRendererOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- field API varies by type
  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- field API varies by type
) => (field: any) => ReactNode;

/**
 * Registry of field renderers by JSON Schema type.
 * Extensible - consumers can register custom renderers for any type.
 */
const fieldRenderers = new Map<string, FieldRenderer>();

/**
 * Renders a numeric field (number or integer) using forms-experimental NumberField.
 */
const renderNumericField: FieldRenderer = (schema, _fieldName, options) => {
  const label = options?.labelOverride ?? (typeof schema.title === 'string' ? schema.title : undefined);
  const labelElement = options?.required && label ? <RequiredLabel isRequired>{label}</RequiredLabel> : label;

  return (field) => (
    <field.NumberField
      label={labelElement}
      description={typeof schema.description === 'string' ? schema.description : undefined}
      minValue={typeof schema.minimum === 'number' ? schema.minimum : undefined}
      maxValue={typeof schema.maximum === 'number' ? schema.maximum : undefined}
      step={typeof schema.multipleOf === 'number' ? schema.multipleOf : undefined}
    />
  );
};

// Register default field renderers for numeric types
fieldRenderers.set('number', renderNumericField);
fieldRenderers.set('integer', renderNumericField);

/**
 * Renders a string field using forms-experimental TextInputField.
 * If schema has `enum`, delegates to renderEnumField for Select rendering.
 */
const renderStringField: FieldRenderer = (schema, fieldName, options) => {
  // If enum is present, use Select instead
  if (schema.enum && Array.isArray(schema.enum)) {
    return renderEnumField(schema, fieldName, options);
  }

  const label = options?.labelOverride ?? (typeof schema.title === 'string' ? schema.title : undefined);
  const labelElement = options?.required && label ? <RequiredLabel isRequired>{label}</RequiredLabel> : label;

  return (field) => (
    <field.TextInputField
      label={labelElement}
      description={typeof schema.description === 'string' ? schema.description : undefined}
    />
  );
};

fieldRenderers.set('string', renderStringField);

/**
 * Renders an enum field (string with enum constraint) using forms-experimental Select.
 */
const renderEnumField: FieldRenderer = (schema, _fieldName, options) => {
  const label = options?.labelOverride ?? (typeof schema.title === 'string' ? schema.title : undefined);
  const labelElement = options?.required && label ? <RequiredLabel isRequired>{label}</RequiredLabel> : label;
  const enumValues = (schema.enum as unknown[]) ?? [];

  return (field) => (
    <field.Select
      label={labelElement}
      description={typeof schema.description === 'string' ? schema.description : undefined}
    >
      {enumValues.map((value) => (
        <field.ListBoxItem key={String(value)} id={String(value)}>
          {String(value)}
        </field.ListBoxItem>
      ))}
    </field.Select>
  );
};

/**
 * Renders a boolean field using forms-experimental Checkbox.
 */
const renderBooleanField: FieldRenderer = (schema, _fieldName, options) => {
  const label = options?.labelOverride ?? (typeof schema.title === 'string' ? schema.title : undefined);
  const description = typeof schema.description === 'string' ? schema.description : undefined;

  return (field) => <field.Checkbox description={description}>{label}</field.Checkbox>;
};

fieldRenderers.set('boolean', renderBooleanField);

/**
 * Renders an object field by iterating over properties and rendering nested fields.
 * Requires `useFormContext` to be passed in options to create nested AppField components.
 */
const renderObjectField: FieldRenderer = (schema, fieldName, options) => {
  const properties = schema.properties as Record<string, JSONSchema> | undefined;
  const requiredFields = Array.isArray(schema.required) ? schema.required : [];
  const formContext = options?.formContext;
  const useFormContext = options?.useFormContext;

  if (!properties) {
    return () => <div>Error: Object schema must have properties</div>;
  }

  if (!formContext) {
    return () => <div>Error: Object renderer requires formContext in options</div>;
  }

  const label = typeof schema.title === 'string' ? schema.title : undefined;
  const description = typeof schema.description === 'string' ? schema.description : undefined;

  // Precompute per-property data once in the factory scope so that validators and
  // render functions are stable references across renders of ObjectFieldRenderer.
  // Doing this inside the render function would create new Zod schemas and new
  // render closures on every render, causing form.AppField to see new props each
  // time and triggering unnecessary re-validation and reconciliation.
  const propFields = Object.entries(properties).flatMap(([propName, propSchema]) => {
    const propType = typeof propSchema.type === 'string' ? propSchema.type : undefined;
    if (!propType) {
      return [];
    }

    const propRenderer = getFieldRenderer(propType);
    if (!propRenderer) {
      return [];
    }

    const nestedFieldName = `${fieldName}.${propName}`;
    const isRequired = requiredFields.includes(propName);
    const renderProp = propRenderer(propSchema, nestedFieldName, {
      required: isRequired,
      useFormContext,
      formContext,
    });
    const validators = { onChange: schemaToZod(propSchema) };
    const mode = propType === 'array' ? ('array' as const) : undefined;
    // Store itemSchema for array properties so ObjectFieldRenderer can compute
    // onChangeListenTo dynamically (needed for uniqueItems validation on item edits).
    const arrayItemSchema = mode === 'array' ? (propSchema.items as JSONSchema | undefined) : undefined;

    return [{ propName, nestedFieldName, renderProp, validators, mode, arrayItemSchema }];
  });

  // Return a render function for the object field.
  // IMPORTANT: This function runs inside TanStack Form's useMemo, so we CANNOT call hooks here.
  // The form context must be passed via options.formContext, not retrieved via useFormContext().
  return function ObjectFieldRenderer() {
    // Use the pre-resolved form context (NOT calling a hook here)
    const form = formContext;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lp-spacing-400)' }}>
        {label && <Text style={{ fontWeight: 600 }}>{label}</Text>}
        {description && (
          <Text slot="description" style={{ fontSize: '0.875rem', color: 'var(--lp-color-text-ui-secondary)' }}>
            {description}
          </Text>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--lp-spacing-400)',
            paddingLeft: 'var(--lp-spacing-400)',
            borderLeft: '2px solid var(--lp-color-border-ui-primary)',
          }}
        >
          {propFields.map(({ propName, nestedFieldName, renderProp, validators, mode, arrayItemSchema }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            // oxlint-disable-next-line typescript-eslint/no-explicit-any
            let effectiveValidators: any = validators;

            if (mode === 'array') {
              // TanStack Form's mode="array" onChange only fires on structural changes
              // (pushValue/removeValue), not when individual items are edited. Adding
              // onChangeListenTo makes the validator re-run on item edits, which is
              // required for uniqueItems to clear its error when duplicates are resolved.
              // Read current array length from form state (no hook — plain object access).
              const parts = nestedFieldName.replace(/\[(\d+)\]/g, '.$1').split('.');
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let arrayVal: unknown = form.state.values;
              for (const part of parts) {
                if (arrayVal === null || arrayVal === undefined || typeof arrayVal !== 'object') {
                  arrayVal = [];
                  break;
                }
                arrayVal = (arrayVal as Record<string, unknown>)[part];
              }
              const arrayLength = Array.isArray(arrayVal) ? arrayVal.length : 0;

              const onChangeListenTo: string[] = [];
              for (let i = 0; i < arrayLength; i++) {
                const itemName = `${nestedFieldName}[${i}]`;
                if (arrayItemSchema?.type === 'object' && arrayItemSchema.properties) {
                  for (const propKey of Object.keys(arrayItemSchema.properties as Record<string, JSONSchema>)) {
                    onChangeListenTo.push(`${itemName}.${propKey}`);
                  }
                } else {
                  onChangeListenTo.push(itemName);
                }
              }

              effectiveValidators = { ...validators, onChangeListenTo };
            }

            return (
              <form.AppField key={propName} name={nestedFieldName} mode={mode} validators={effectiveValidators}>
                {renderProp}
              </form.AppField>
            );
          })}
        </div>
      </div>
    );
  };
};

fieldRenderers.set('object', renderObjectField);

/**
 * Renders an array field with add/remove capabilities.
 * Requires `useFormContext` to be passed in options to create nested AppField components.
 *
 * Uses TanStack Form's array field pattern:
 * - The array field itself uses mode="array" (handled in JsonSchemaFieldFormComponent)
 * - Each item is rendered via a nested AppField with indexed name (e.g., `items[0]`)
 */
const renderArrayField: FieldRenderer = (schema, fieldName, options) => {
  const itemSchema = schema.items as JSONSchema | undefined;
  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;
  const maxItems = typeof schema.maxItems === 'number' ? schema.maxItems : Infinity;
  const formContext = options?.formContext;
  const useFormContext = options?.useFormContext;

  if (!itemSchema) {
    return () => <div>Error: Array schema must have items</div>;
  }

  if (!formContext) {
    return () => <div>Error: Array renderer requires formContext in options</div>;
  }

  const itemType = typeof itemSchema.type === 'string' ? itemSchema.type : undefined;
  if (!itemType) {
    return () => <div>Error: Array items must have a type</div>;
  }

  // Hoist stable computations to factory scope so they are not recreated on every render.
  // itemRenderer and itemValidator are constant for a given schema configuration.
  const itemRendererFn = getFieldRenderer(itemType);
  if (!itemRendererFn) {
    return () => <div>Error: Unsupported array item type: {itemType}</div>;
  }
  const itemValidators = { onChange: schemaToZod(itemSchema) };

  const label = typeof schema.title === 'string' ? schema.title : undefined;
  const labelElement = options?.required && label ? <RequiredLabel isRequired>{label}</RequiredLabel> : label;
  const description = typeof schema.description === 'string' ? schema.description : undefined;
  const itemTitle = typeof itemSchema.title === 'string' ? itemSchema.title : 'Item';

  // Use refs passed from ArraySchemaField so these values survive the factory being
  // re-called on every render. Fallback to plain objects for call-sites that don't
  // supply refs (e.g. tests) — those callers accept the remount behaviour.
  const stableKeys: string[] = options?.stableKeysRef?.current ?? [];
  const keyCounterRef = options?.keyCounterRef ?? { current: 0 };
  const newlyAddedIndexRef = options?.newlyAddedIndexRef ?? { current: null };

  // Return a render function for the array field.
  // IMPORTANT: This function runs inside TanStack Form's useMemo, so we CANNOT call hooks here.
  // The form context must be passed via options.formContext, not retrieved via useFormContext().
  return function ArrayFieldRenderer(field: {
    state: {
      value: unknown[];
      meta: { isTouched: boolean; errors: unknown[] };
    };
    pushValue: (value: unknown) => void;
    removeValue: (index: number) => void;
  }) {
    // Use the pre-resolved form context (NOT calling a hook here)
    const form = formContext;

    const items = field.state.value ?? [];
    const canAdd = items.length < maxItems;
    const canRemove = items.length > minItems;
    const isEmpty = items.length === 0;

    const hasErrors = field.state.meta.isTouched && field.state.meta.errors.length > 0;
    const errorMessage = hasErrors ? formatFieldErrors(field.state.meta.errors) : undefined;

    // Sync stable keys with current item count (handles initial items from defaultValues
    // and any external state changes)
    while (stableKeys.length < items.length) {
      stableKeys.push(`item-${keyCounterRef.current++}`);
    }
    if (stableKeys.length > items.length) {
      stableKeys.length = items.length;
    }

    // Capture and immediately reset auto-focus index so subsequent re-renders
    // (e.g. typing in a field) don't re-steal focus
    const autoFocusIndex = newlyAddedIndexRef.current;
    newlyAddedIndexRef.current = null;

    const handleAdd = () => {
      const defaultValue = getDefaultValue(itemSchema);
      newlyAddedIndexRef.current = items.length;
      stableKeys.push(`item-${keyCounterRef.current++}`);
      field.pushValue(defaultValue);
    };

    return (
      <ArrayFieldWrapper
        label={labelElement}
        description={description}
        errorMessage={errorMessage}
        onAdd={handleAdd}
        canAdd={canAdd}
        isEmpty={isEmpty}
      >
        {items.map((_, i) => {
          const itemFieldName = `${fieldName}[${i}]`;
          const itemLabel = `${itemTitle} ${i + 1}`;
          // itemRendererFn and itemValidators are stable (hoisted to factory scope).
          // Only labelOverride and itemFieldName are per-item and must be computed here.
          const renderItem = itemRendererFn(itemSchema, itemFieldName, {
            useFormContext,
            formContext,
            labelOverride: itemLabel,
          });

          return (
            <ArrayItemRow
              key={stableKeys[i]}
              index={i}
              onRemove={() => {
                stableKeys.splice(i, 1);
                field.removeValue(i);
              }}
              canRemove={canRemove}
              autoFocus={autoFocusIndex === i}
            >
              <form.AppField name={itemFieldName} validators={itemValidators}>
                {renderItem}
              </form.AppField>
            </ArrayItemRow>
          );
        })}
      </ArrayFieldWrapper>
    );
  };
};

fieldRenderers.set('array', renderArrayField);

/**
 * Register a custom field renderer for a JSON Schema type.
 *
 * @param type - The JSON Schema type (e.g., 'string', 'boolean', 'object')
 * @param renderer - A function that renders the field
 */
export function registerFieldRenderer(type: string, renderer: FieldRenderer): void {
  fieldRenderers.set(type, renderer);
}

/**
 * Get the field renderer for a JSON Schema type.
 *
 * @param type - The JSON Schema type
 * @returns The field renderer, or undefined if not registered
 */
export function getFieldRenderer(type: string): FieldRenderer | undefined {
  return fieldRenderers.get(type);
}

/**
 * Check if a field renderer is registered for a JSON Schema type.
 *
 * @param type - The JSON Schema type
 * @returns true if a renderer is registered
 */
export function hasFieldRenderer(type: string): boolean {
  return fieldRenderers.has(type);
}

/**
 * Get all registered field renderer types.
 *
 * @returns Array of registered type names
 */
export function getRegisteredTypes(): string[] {
  return Array.from(fieldRenderers.keys());
}

/**
 * Clear all registered field renderers.
 * Primarily useful for testing.
 */
export function clearFieldRenderers(): void {
  fieldRenderers.clear();
}

/**
 * Reset field renderers to default state (numeric, string, boolean, object, array types).
 * Primarily useful for testing.
 */
export function resetFieldRenderers(): void {
  fieldRenderers.clear();
  fieldRenderers.set('number', renderNumericField);
  fieldRenderers.set('integer', renderNumericField);
  fieldRenderers.set('string', renderStringField);
  fieldRenderers.set('boolean', renderBooleanField);
  fieldRenderers.set('object', renderObjectField);
  fieldRenderers.set('array', renderArrayField);
}
